import React from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import type { RoomRateRoom } from './utils';

interface Props {
  appliedRatePlanNamesByRoomTypeId: Record<string, Array<string>>;
  createdRatePlans: Array<App.RatePlan>;
  disabled: boolean;
  onClearRooms: () => void;
  onSelectAllRooms: () => void;
  onSelectRatePlans: (roomTypeId: string, ratePlanIds: Array<string>) => void;
  readOnly?: boolean;
  rooms: Array<RoomRateRoom>;
  selectedRatePlanIds: Record<string, Array<string>>;
}

function getRatePlanSummary(ratePlans: Array<App.RatePlan>) {
  if (ratePlans.length === 0) {
    return 'the new Flash rate plans';
  }

  if (ratePlans.length === 1) {
    return ratePlans[0].name || 'the new Flash rate plan';
  }

  return `${ratePlans.length} Flash rate plans`;
}

function getReadOnlyRatePlanLabels(
  roomTypeId: string,
  appliedRatePlanNamesByRoomTypeId: Record<string, Array<string>>,
  createdRatePlans: Array<App.RatePlan>,
  selectedRatePlanIds: Record<string, Array<string>>,
) {
  const appliedNames = appliedRatePlanNamesByRoomTypeId[roomTypeId];

  if (appliedNames?.length) {
    return appliedNames;
  }

  const selectedRatePlanIdsForRoom = selectedRatePlanIds[roomTypeId] ?? [];

  return createdRatePlans
    .filter((ratePlan) => selectedRatePlanIdsForRoom.includes(ratePlan.id))
    .map((ratePlan) => ratePlan.name || ratePlan.id);
}

export default function RoomRatesStep({
  appliedRatePlanNamesByRoomTypeId,
  createdRatePlans,
  disabled,
  onClearRooms,
  onSelectAllRooms,
  onSelectRatePlans,
  readOnly = false,
  rooms,
  selectedRatePlanIds,
}: Props) {
  const selectableRooms = rooms.filter((room) => !room.hasRatePlan);
  const selectedRoomCount = readOnly
    ? rooms.filter(
        (room) =>
          getReadOnlyRatePlanLabels(room.id, appliedRatePlanNamesByRoomTypeId, createdRatePlans, selectedRatePlanIds)
            .length > 0,
      ).length
    : Object.keys(selectedRatePlanIds).length;
  const allSelectableRoomsSelected =
    selectableRooms.length > 0 && selectableRooms.every((room) => selectedRatePlanIds[room.id]?.length > 0);
  const ratePlanSummary = getRatePlanSummary(createdRatePlans);

  if (createdRatePlans.length === 0) {
    return <Alert severity="error">Create at least one Flash rate plan before applying room rates.</Alert>;
  }

  if (rooms.length === 0) {
    return <Alert severity="warning">No rooms are available for this property.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        {readOnly
          ? `${ratePlanSummary} have already been applied. Review the assignments below, or continue to the next step.`
          : 'Choose which Flash rate plans to apply to each room. All rate plans are selected by default.'}
      </Alert>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ px: 2, py: 1.25, bgcolor: 'grey.50' }}
        >
          <Stack spacing={0.25}>
            <Typography variant="subtitle2">Rooms</Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedRoomCount} of {readOnly ? rooms.length : selectableRooms.length} rooms selected
            </Typography>
          </Stack>
          {!readOnly && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                size="small"
                onClick={onSelectAllRooms}
                disabled={disabled || selectableRooms.length === 0 || allSelectableRoomsSelected}
              >
                Select all rooms
              </Button>
              <Button variant="text" size="small" onClick={onClearRooms} disabled={disabled || selectedRoomCount === 0}>
                Clear
              </Button>
            </Stack>
          )}
        </Stack>
        <Divider />
        {rooms.map((room) => {
          const readOnlyRatePlanLabels = getReadOnlyRatePlanLabels(
            room.id,
            appliedRatePlanNamesByRoomTypeId,
            createdRatePlans,
            selectedRatePlanIds,
          );

          return (
            <Box key={room.id}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  width: '100%',
                  minHeight: 64,
                  px: 2,
                  py: 1,
                  borderLeft: 3,
                  borderColor:
                    readOnly || selectedRatePlanIds[room.id]?.length || room.hasRatePlan
                      ? 'primary.main'
                      : 'transparent',
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: room.hasRatePlan || disabled || readOnly ? undefined : 'action.hover',
                  },
                }}
              >
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{room.name}</Typography>
                  {room.hasRatePlan && !readOnly && (
                    <Typography variant="caption" color="text.secondary">
                      All rate plans already applied
                    </Typography>
                  )}
                </Stack>
                {readOnly ? (
                  <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="flex-end" sx={{ maxWidth: '60%' }}>
                    {readOnlyRatePlanLabels.length > 0 ? (
                      readOnlyRatePlanLabels.map((label) => (
                        <Chip key={label} label={label} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No rate plans applied
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  !room.hasRatePlan && (
                    <FormControl size="small" sx={{ width: 280, flexShrink: 0 }}>
                      <InputLabel id={`flash-room-rate-plan-${room.id}`}>Rate plan</InputLabel>
                      <Select
                        label="Rate plan"
                        labelId={`flash-room-rate-plan-${room.id}`}
                        multiple
                        value={selectedRatePlanIds[room.id] ?? []}
                        disabled={disabled}
                        renderValue={(selected) =>
                          createdRatePlans
                            .filter((ratePlan) => selected.includes(ratePlan.id))
                            .map((ratePlan) => ratePlan.name || ratePlan.id)
                            .join(', ')
                        }
                        onChange={(event) => {
                          const value = event.target.value;
                          onSelectRatePlans(room.id, typeof value === 'string' ? value.split(',') : value);
                        }}
                      >
                        {createdRatePlans.map((ratePlan) => (
                          <MenuItem key={ratePlan.id} value={ratePlan.id}>
                            <Checkbox checked={selectedRatePlanIds[room.id]?.includes(ratePlan.id) ?? false} />
                            {ratePlan.name || ratePlan.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                )}
              </Stack>
              <Divider />
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
